import InternalInterviewParticipantView from '@UI/Pages/Beneficiaries/Hooks/useBeneficiaryInterviewsList/InternalInterviewParticipantView';
import type InterviewView from '@UI/Store/Interviews/Views/InterviewView';

/**
 * The two referentials a server entry's bare participant ids are resolved against, keyed by id.
 * A required argument, never optional: an entry missing from either map falls back to a degraded
 * label (`Contact n°{id}` / `Membre du personnel n°{id}`) rather than to a nude id — the same motif
 * as `StaffAssignmentPanel.tsx`'s own fallback for a staff member outside the caller's read scope.
 */
export type InterviewParticipantLabels = {
	readonly contactNameById: ReadonlyMap<number, string>;
	readonly staffMemberNameById: ReadonlyMap<number, string>;
};

/**
 * Resolves a server entry's bare participant ids against the two loaded referentials. The server
 * only ever sends identifiers (a relation sends its id and nothing else) — never a name — so this is
 * the one place a server-origin entry's labels are built, always with a degraded fallback rather than
 * a failed lookup: an id absent from its map means either referential has not loaded yet, or the
 * caller has no read access to that particular member, a nominal case the server produces on purpose.
 */
const resolveServerParticipants = (
	serverInterview: InterviewView,
	labels: InterviewParticipantLabels,
): InternalInterviewParticipantView[] => {
	const contactEntries = serverInterview.participantContactIds.map(
		(id) =>
			new InternalInterviewParticipantView({
				kind: 'contact',
				id,
				label: labels.contactNameById.get(id) ?? `Contact n°${id}`,
			}),
	);

	const staffEntries = serverInterview.participantStaffMemberIds.map(
		(id) =>
			new InternalInterviewParticipantView({
				kind: 'staff',
				id,
				label: labels.staffMemberNameById.get(id) ?? `Membre du personnel n°${id}`,
			}),
	);

	return [...contactEntries, ...staffEntries];
};

export default resolveServerParticipants;
